import React, { useEffect, useState, useRef } from "react";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import EnvelopeIcon from "@styles/all-images/icons/envelope-icon.svg";
import PhoneIcon from "@styles/all-images/icons/phone-icon.svg";
import EditIcon from "@styles/all-images/icons/edit-icon.svg";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import actionsHandler from "@/store/actions/actionHandler";
import { RootState, AppDispatch } from "@/store";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { S3Image, getS3FileUrl, useS3File } from "@/utils/s3-helper";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import RenderOtherDetails from "./RenderOtherDetails";

interface CommodityType {
  id: number;
  title: string;
}
interface MasterProfile {
  id: number;
  name: string;
}
interface UserDetails {
  id: number;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  name: string;
  user_type: {
    user_type: string;
    id: number;
  };
  profile_picture: string | null;
  is_staff: boolean;
  accept_tc: boolean;
  is_active: boolean;
  status: number;
  companies: [
    {
      id: number;
      name: string;
      trading_as: string;
      commodity_types: CommodityType[];
      administrative_first_name: string;
      administrative_last_name: string;
      administrative_email: string;
      administrative_contact_number: string;
      administrative_profiles: MasterProfile[];
      designation: string;
      facility_required: boolean;
      facility_amount: number;
      debtor_type: {
        debtor_type: string;
        id: number;
      };
      payment_terms: {
        payment_term: string;
        id: number;
      };
      client_type: {
        client_type: string;
        id: number;
      };
      profession: {
        title: string;
        id: number;
      };
      reason_to_access: string;
      custom_payment_days: number;
      credit_facility: string | null;
    },
  ];
  created_by: number;
  updated_by: number;
  deleted_by: number | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  message: string;
}

interface MyAccountProps {
  userId?: string;
  isCustomView?: boolean;
}

const MyAccount = ({ userId, isCustomView }: MyAccountProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const { loading } = useSelector((state: RootState) => state.auth);
  const user = userId || JSON.parse(localStorage.getItem("user") || "{}")?.id;
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const getUserDetails = async () => {
      try {
        const { authenticatedCall: fetchUserDetails } = actionsHandler(
          userId ? "userByID" : "userProfileDetail",
        );
        const response = await dispatch(
          fetchUserDetails({
            method: "get",
            customParams: userId ? [userId + "/"] : undefined,
          }),
        );

        if (response.ok) {
          setUserDetails(response.data as UserDetails);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };

    if (user) {
      getUserDetails();
    }
  }, [dispatch, user]);

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("profile_picture", file);

      const { authenticatedCall } = actionsHandler("updateProfilePicture");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: formData,
          customHeader: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }),
      );

      if (response.ok) {
        toast.success(
          response.message || "Profile picture updated successfully",
        );
        const { authenticatedCall: fetchUserDetails } =
          actionsHandler("userProfileDetail");
        const userResponse = await dispatch(
          fetchUserDetails({
            method: "get",
          }),
        );

        if (userResponse.ok) {
          setUserDetails(userResponse.data as UserDetails);
        }
      }
    } catch (error) {
      console.error("Error updating profile picture:", error);
    }
  };

  if (loading || !userDetails) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
        <CustomLoader loading={true} isWrapper={true} size="50" />
      </div>
    );
  }

  return (
    <div
      className={`tw-bg-gray-50 tw-font-sans ${isCustomView ? "tw-bg-white" : ""}`}
    >
      {/* Header Section */}
      {!isCustomView && (
        <>
          <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
            <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b border-[#E9EAEB]">
              <div className="tw-flex tw-justify-between tw-items-center">
                <div className="tw-flex tw-items-center">
                  <a href="/">
                    <img src={BackIcon} alt="Back Icon" />{" "}
                  </a>
                  <h1 className="tw-text-xl tw-ml-4 tw-font-semibold font-primary-color">
                    {t("myAccount.title")}
                  </h1>
                </div>
                <button
                  onClick={() => navigate("/edit-my-account")}
                  className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
                >
                  {t("myAccount.editDetails")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Content Section */}
      <div
        className={`tw-flex tw-flex-col lg:tw-flex-row tw-gap-6 tw-px-8 tw-py-4 ${
          isCustomView ? "" : "tw-mt-[157px]"
        }`}
      >
        {/* Left Profile Section */}
        {!isCustomView && (
          <div className="lg:tw-fixed tw-top-[175px] lg:tw-z-50">
            <div className="tw-bg-[#FBF8FF] tw-rounded-[8px] tw-border-[#87C1DB] tw-border tw-w-full lg:tw-w-[260px] tw-p-8 tw-h-fit">
              {/* Profile Image */}
              <div className="tw-mb-4 tw-flex tw-items-center tw-justify-center">
                <div className="tw-border tw-border-[#E9EAEB] tw-bg-white tw-rounded-full tw-w-24 tw-h-24 tw-flex tw-items-center tw-justify-center tw-relative tw-z-0">
                  <S3Image
                    src={
                      userDetails.profile_picture?.replace(
                        "https%3A/9spl-sentinel.s3.us-east-1.amazonaws.com/",
                        "",
                      ) || null
                    }
                    alt="Profile"
                    cacheKey={userDetails.profile_picture || undefined}
                    className="tw-w-12 tw-h-12"
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="tw-hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <button
                    onClick={handleProfilePictureClick}
                    className="tw-absolute tw-bottom-0 tw-right-0 tw-bg-[#146C94] tw-text-white tw-p-1 tw-rounded-full tw-z-50"
                  >
                    <img src={EditIcon} alt="Edit" className="tw-w-4 tw-h-4" />
                  </button>
                </div>
              </div>
              {/* User Info */}
              <div className="tw-flex tw-items-center tw-justify-between tw-flex-col">
                <h2 className="tw-text-lg tw-font-semibold font-primary font-default-primary-color">
                  {userDetails?.first_name
                    ? `${userDetails.first_name} ${userDetails.last_name}`
                    : userDetails?.name || ""}
                </h2>
                <p className="tw-flex tw-items-center font-primary-color tw-text-sm tw-mt-2">
                  <img
                    src={EnvelopeIcon}
                    alt="Envelope"
                    className="tw-mr-2 tw-w-4 tw-h-4"
                  />
                  {userDetails.email}
                </p>
                <p className="tw-flex tw-items-center font-primary-color tw-text-sm tw-mt-1">
                  <img
                    src={PhoneIcon}
                    alt="Phone"
                    className="tw-mr-2 tw-w-4 tw-h-4"
                  />
                  {userDetails.phone}
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Right Details Section */}
        <div
          className={`tw-w-full lg:tw-w-full tw-ml-0 ${isCustomView ? "" : "lg:tw-ml-[280px]"}`}
        >
          {isCustomView && (
            <div className="tw-bg-white custom-card">
              {/* Primary Details */}
              <div>
                <div className="custom-card-header">
                  <h3 className="font-size-base tw-font-semibold font-primary-color">
                    {t("myAccount.primaryDetails.title")}
                  </h3>
                </div>
                <div className="custom-card-body">
                  <div className="tw-grid tw-grid-cols-2 tw-gap-6">
                    <div>
                      <p className="tw-text-sm font-gray-color tw-font-medium">
                        {t("myAccount.primaryDetails.name")}
                      </p>
                      <p className="tw-font-medium font-primary-color tw-mt-1">
                        {userDetails.first_name
                          ? `${userDetails.first_name} ${userDetails.last_name}`
                          : userDetails?.name || ""}
                      </p>
                    </div>
                    <div>
                      <p className="tw-text-sm font-gray-color tw-font-medium">
                        {t("myAccount.primaryDetails.email")}
                      </p>
                      <p className="tw-font-medium font-primary-color tw-mt-1">
                        {userDetails.email}
                      </p>
                    </div>
                    <div>
                      <p className="tw-text-sm font-gray-color tw-font-medium">
                        {t("myAccount.primaryDetails.phone")}
                      </p>
                      <p className="tw-font-medium font-primary-color tw-mt-1">
                        {userDetails.phone}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="tw-bg-white custom-card">
            {/* Company Details */}
            <div>
              <div className="custom-card-header">
                <h3 className="font-size-base tw-font-semibold font-primary-color">
                  {t("myAccount.companyDetails.title")}
                </h3>
              </div>
              <div className="custom-card-body">
                <div className="tw-grid tw-grid-cols-2 tw-gap-6">
                  <div>
                    <p className="tw-text-sm font-gray-color tw-font-medium">
                      {t("myAccount.companyDetails.registeredName")}
                    </p>
                    <p className="tw-font-medium font-primary-color tw-mt-1">
                      {userDetails.companies?.length > 0
                        ? userDetails.companies[0].name
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="tw-text-sm font-gray-color tw-font-medium">
                      {t("myAccount.companyDetails.tradingAs")}
                    </p>
                    <p className="tw-font-medium font-primary-color tw-mt-1">
                      {userDetails.companies?.length > 0
                        ? userDetails.companies[0].trading_as
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="tw-text-sm font-gray-color tw-font-medium">
                      {t("myAccount.companyDetails.commodityType")}
                    </p>
                    <p className="tw-font-medium font-primary-color tw-mt-1">
                      {userDetails.companies?.length > 0 &&
                      userDetails.companies[0].commodity_types.length > 0
                        ? userDetails.companies[0].commodity_types
                            .map((commodity: CommodityType) => commodity.title)
                            .join(", ")
                        : "-"}

                      {/* {userDetails.companies?.length > 0 ? userDetails.companies[0].commodity_type : '-'} */}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-bg-white custom-card tw-my-4">
            {/* Administrative Details */}
            <div>
              <div className="custom-card-header">
                <h3 className="font-size-base tw-font-semibold font-primary-color">
                  {t("myAccount.administrativeDetails.title")}
                </h3>
              </div>
              <div className="custom-card-body">
                <div className="tw-grid tw-grid-cols-2 tw-gap-6">
                  <div>
                    <p className="tw-text-sm font-gray-color tw-font-medium">
                      {t("myAccount.administrativeDetails.name")}
                    </p>
                    <p className="tw-font-medium font-primary-color tw-mt-1">
                      {userDetails.companies?.length > 0
                        ? `${userDetails.companies[0].administrative_first_name} ${userDetails.companies[0].administrative_last_name}`
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="tw-text-sm font-gray-color tw-font-medium">
                      {t("myAccount.administrativeDetails.email")}
                    </p>
                    <p className="tw-font-medium font-primary-color tw-mt-1">
                      {userDetails.companies?.length > 0
                        ? userDetails.companies[0].administrative_email
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="tw-text-sm font-gray-color tw-font-medium">
                      {t("myAccount.administrativeDetails.phone")}
                    </p>
                    <p className="tw-font-medium font-primary-color tw-mt-1">
                      {userDetails.companies?.length > 0
                        ? userDetails.companies[0].administrative_contact_number
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="tw-text-sm font-gray-color tw-font-medium">
                      {t("myAccount.administrativeDetails.role")}
                    </p>
                    <p className="tw-font-medium font-primary-color tw-mt-1">
                      {userDetails.companies?.length > 0
                        ? userDetails.companies[0].designation
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="tw-text-sm font-gray-color tw-font-medium">
                      {t("myAccount.administrativeDetails.masterProfiles")}
                    </p>
                    <p className="tw-font-medium font-primary-color tw-mt-1">
                      {userDetails.companies?.length > 0 &&
                      userDetails.companies[0]?.administrative_profiles
                        ? userDetails.companies[0]?.administrative_profiles
                            ?.map((profile: MasterProfile) => profile.name)
                            .join(", ")
                        : "-"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Other Details (Tier Specific) */}
          {[4, 5, 6, 7].includes(userDetails.user_type.id) && (
            <div className="tw-bg-white custom-card tw-my-4">
              <div className="custom-card-header">
                <h3 className="font-size-base tw-font-semibold font-primary-color">
                  {t("myAccount.otherDetails.title")}
                </h3>
              </div>
              <div className="custom-card-body">
                <RenderOtherDetails userDetails={userDetails} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAccount;
